import { TileTypes } from "@/types/review.types";
import { ReviewTileItem } from "@/types/tile.types";
import { QUOTE_CHARS } from "@/hooks/locate-text.policy";

// Safely read a string-valued field from a loosely-typed tile. Historical
// compare-precedent tiles carry fields (missingClause, additionalClause,
// legislationText, otherText, precedentText) that are absent from the strict
// TileTypes union but may still appear in stored-review payloads.
function readStringField(data: TileTypes, key: string): string {
  const value = (data as unknown as Record<string, unknown>)[key];
  return typeof value === "string" ? value : "";
}

function extractTextByQuery(finalQuery: string, extraData: TileTypes): string {
  switch (finalQuery) {
    case "risk-analysis":
      return "riskText" in extraData ? (extraData.riskText ?? "") : "";
    case "ambiguities":
      return "ambiguousText" in extraData ? (extraData.ambiguousText ?? "") : "";
    case "complex-language":
      return "complexText" in extraData ? (extraData.complexText ?? "") : "";
    case "custom-review":
      return "relevantText" in extraData ? (extraData.relevantText ?? "") : "";
    case "conflicting-terms":
      return "clause1" in extraData ? (extraData.clause1?.relevant_text ?? "") : "";
    case "find-placeholders":
      return "placeholder" in extraData ? (extraData.placeholder ?? "") : "";
    case "inconsistencies":
      // Compare-precedent inconsistency tiles carry precedentText/otherText;
      // regular inconsistency tiles carry text1/text2.
      return (
        readStringField(extraData, "precedentText") ||
        readStringField(extraData, "otherText") ||
        readStringField(extraData, "text1") ||
        readStringField(extraData, "text2")
      );
    case "missing-clauses":
      // Regular missing-clauses use insertionPoint; compare-precedent stored
      // reviews use otherText/missingClause. Check both so "Find in document"
      // still works on historical compare-precedent tiles.
      return (
        readStringField(extraData, "insertionPoint") ||
        readStringField(extraData, "otherText") ||
        readStringField(extraData, "missingClause")
      );
    case "non-standard-clauses":
      return "nonStandardText" in extraData ? (extraData.nonStandardText ?? "") : "";
    case "points-to-negotiate":
      return "negotiationText" in extraData ? (extraData.negotiationText ?? "") : "";
    case "cross-referencing-clauses":
    case "sequential-order":
      return "context" in extraData ? (extraData.context ?? "") : "";
    case "undefined-terms":
      return "contextText" in extraData ? (extraData.contextText ?? "") : "";
    // Compare-precedent-only query values. New reviews do not emit these
    // (compare precedent is hidden from the generate review form), but stored
    // historical reviews can still render and be searched against.
    case "additional-clauses":
      return readStringField(extraData, "additionalClause");
    case "unfavorable-terms":
    case "general-comparison":
      return readStringField(extraData, "otherText");
    case "legislation-check":
      return readStringField(extraData, "legislationText");
    default:
      return "";
  }
}

export const useTileTextExtraction = () => {
  const getSearchableText = (item: ReviewTileItem): string => {
    if (!item.originalData) return "";
    const query = item.query ?? "";
    return extractTextByQuery(query, item.originalData);
  };

  const normalizeSearchText = (text: string): string => {
    if (!text || typeof text !== "string") return "";
    return text
      .trim()
      .replace(QUOTE_CHARS, "")
      .replace(/\s+/g, " ")
      .replace(/\n+/g, "\n")
      .replace(/\t+/g, " ");
  };

  return {
    getSearchableText,
    normalizeSearchText,
  };
};
